import AgentExplainerLayout, { Section, PointList } from "@/components/marketing/AgentExplainerLayout";
import { AgentHeroVisual, AICallFlow } from "@/components/marketing/AgentFlowDiagram";
import { Link } from "react-router-dom";

export default function AIAgents() {
  return (
    <AgentExplainerLayout
      active="ai"
      testId="ai-agents-page"
      eyebrow="Agents · AI"
      title="AI Agents that listen, talk, and close like a trained rep."
      lead={(n) =>
        `AI Agents are live conversational workers on the phone. In ${n} they greet, follow your call script, use knowledge bases, pause when the customer talks, and exit to book, transfer, or hang up—without a seat license for every parallel line.`
      }
      heroVisual={<AgentHeroVisual variant="ai" />}
    >
      <Section eyebrow="What they are" title="A call-center seat that scales with dials" testId="ai-what">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="space-y-4 text-muted-foreground">
            <p>
              Unlike Voice Agents, an AI Agent keeps a duplex Live session open: it hears the caller,
              reasons with your instructions and knowledge base, and speaks back in real time.
              Outcomes are first-class tools—book appointment, collect info, transfer, hangup—
              so the campaign flow can continue after the conversation.
            </p>
            <PointList
              items={[
                { title: "Script + personality", body: "Objective, rules, and tone stay consistent on every lead." },
                { title: "Grounded answers", body: "Attach a knowledge base so claims stay on-message." },
                { title: "Human handoff", body: "Warm SIP transfer when the deal needs a person." },
              ]}
            />
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-lg">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,hsl(var(--primary)/0.15),transparent_45%)]" />
            <p className="disp relative text-lg font-bold">Replacing call-center load</p>
            <ul className="relative mt-4 space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-2"><span className="text-primary">▸</span> Same pitch at 2 a.m. and noon—no shift gaps</li>
              <li className="flex gap-2"><span className="text-primary">▸</span> Instant language switch without hiring bilingual benches</li>
              <li className="flex gap-2"><span className="text-primary">▸</span> Parallel capacity limited by engines, not headcount</li>
              <li className="flex gap-2"><span className="text-primary">▸</span> Every turn logged for QA, coaching, and disputes</li>
              <li className="flex gap-2"><span className="text-primary">▸</span> Humans stay on high-value closes and exceptions</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section eyebrow="Call flow" title="How an AI Agent call moves" testId="ai-flow">
        <p className="mb-6 max-w-2xl text-muted-foreground">
          Connect, live conversation, structured exit. The diagram mirrors what operators see when an AI node is running.
        </p>
        <AICallFlow />
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[
            ["Open", "AMD / answer gates junk. The Live model greets from your script—not a free improvisation."],
            ["Work the lead", "Listen, ask one question at a time, apply conversation rules, pause on barge-in."],
            ["Exit cleanly", "Tool actions move the canvas: book → collect, transfer → SIP, hangup → end."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-xl border border-border bg-card p-4">
              <p className="disp font-semibold">{t}</p>
              <p className="mt-2 text-sm text-muted-foreground">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Benefits" title="Why teams move volume onto AI Agents" testId="ai-benefits">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ["Coverage", "Nights, weekends, and burst campaigns without overtime."],
            ["Consistency", "No skipped disclosures or forgotten rebuttals."],
            ["Speed", "No hold music while a queue rings—AI answers immediately."],
            ["Multilingual", "Serve markets your staffing plan cannot yet cover."],
            ["Measurable", "Transcripts, actions, and dispositions land in CDR."],
            ["Composable", "Drop an AI node beside Voice nodes—or escalate to Hybrid."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-2xl border border-border bg-gradient-to-b from-card to-muted/20 p-5">
              <p className="disp font-semibold">{t}</p>
              <p className="mt-2 text-sm text-muted-foreground">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Going deeper" title="What “replace a call-center agent” really means" testId="ai-deeper">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              AI Agents excel at structured outbound and inbound where the playbook is clear:
              introduce the company, qualify interest, handle common objections, capture fields,
              and book or transfer. They do not replace judgment-heavy negotiations, regulated
              advice that must be human-signed, or emotionally charged escalations.
            </p>
            <p>
              The winning pattern is not “fire the floor”—it is “let AI work the top of the funnel
              so humans only talk to people who are ready.” That is exactly what{" "}
              <Link to="/agents/hybrid" className="font-semibold text-primary hover:underline">Hybrid Agents</Link>
              {" "}formalize.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="disp font-semibold">Operate with guardrails</p>
            <PointList
              items={[
                "Write a clear objective and hard rules (no inventing prices)",
                "Attach KB articles for product facts",
                "Allow continue so the model can listen before exiting",
                "Cap duration and inactivity so stuck calls release capacity",
                "Always keep a transfer path for angry or complex callers",
              ]}
            />
          </div>
        </div>
      </Section>
    </AgentExplainerLayout>
  );
}
